interface RetryOptions {
  maxRetries: number;
  baseDelayMs: number;
  shouldRetry: (error: unknown) => boolean;
}

const MAX_DELAY_MS = 30000;

const DEFAULT_OPTIONS: RetryOptions = {
  maxRetries: 3,
  baseDelayMs: 1000,
  shouldRetry: isTransientHttpError,
};

export function isTransientHttpError(error: unknown): boolean {
  if (error instanceof Response) {
    return [502, 503, 504].includes(error.status);
  }

  if (typeof error === "object" && error !== null) {
    const statusError = error as { status?: number; response?: { status?: number } };
    const status = statusError.status ?? statusError.response?.status;
    if (status !== undefined && [502, 503, 504].includes(status)) {
      return true;
    }
  }

  if (error instanceof TypeError) {
    const message = error.message.toLowerCase();
    if (message.includes("fetch") || message.includes("network")) {
      return true;
    }
  }

  if (error instanceof Error) {
    const message = error.message;
    if (message.includes("ECONNRESET") || message.includes("ETIMEDOUT")) {
      return true;
    }
  }

  return false;
}

export async function withRetry<T>(
  fn: () => Promise<T>,
  options?: Partial<RetryOptions>
): Promise<T> {
  const opts: RetryOptions = { ...DEFAULT_OPTIONS, ...options };

  let lastError: unknown;

  for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;

      if (attempt === opts.maxRetries || !opts.shouldRetry(error)) {
        throw error;
      }

      const delay = Math.min(opts.baseDelayMs * Math.pow(2, attempt), MAX_DELAY_MS);
      console.warn(
        `[retry] Attempt ${attempt + 1}/${opts.maxRetries} failed, retrying in ${delay}ms...`,
        error
      );
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }

  throw lastError;
}
